import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, openUploadedFile } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/formatDate';
import { AuditTrail } from '../components/admin/AuditTrail';
import {
  maskPAN,
  maskAadhaar,
  maskBankAccount,
  maskGSTIN,
  canToggleSensitiveValue,
  displaySensitiveValue,
} from '../utils/validations';
import {
  ArrowLeft, Building2, MapPin, User, Wallet, FileText, Tags, FileCheck,
  CheckCircle2, XCircle, MessageSquare, Clock, Download, LogOut, Loader2,
  Play, Eye, EyeOff
} from 'lucide-react';

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'status-submitted', icon: Clock },
  under_review: { label: 'Under Review (DA)', color: 'status-under-review', icon: Clock },
  pending_ar: { label: 'Pending AR Review', color: 'bg-purple-100 text-purple-800', icon: Clock },
  pending_hos: { label: 'Pending HoS Approval', color: 'bg-indigo-100 text-indigo-800', icon: Clock },
  query_raised: { label: 'Query Raised', color: 'status-query-raised', icon: MessageSquare },
  approved: { label: 'Approved', color: 'status-approved', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'status-rejected', icon: XCircle },
};

const REJECTION_REASONS = [
  { value: 'Incomplete Documentation', label: 'Incomplete Documentation' },
  { value: 'Financial Non-Compliance', label: 'Financial Non-Compliance' },
  { value: 'Blacklisting History', label: 'Blacklisting History' },
  { value: 'Eligibility Criteria Not Met', label: 'Eligibility Criteria Not Met' },
  { value: 'False Declaration', label: 'False Declaration' },
  { value: 'Other', label: 'Other (Specify in explanation)' },
];

const SECTIONS_FOR_QUERY = [
  { value: 'basic_details', label: 'Basic Details' },
  { value: 'address', label: 'Address & Contact' },
  { value: 'signatory', label: 'Authorized Signatory' },
  { value: 'bank_details', label: 'Financial & Bank Details' },
  { value: 'documents', label: 'Compliance Documents' },
  { value: 'capability', label: 'Category & Capability' },
  { value: 'declaration', label: 'Declarations' },
];

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, canApprove } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [queries, setQueries] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showForwardARModal, setShowForwardARModal] = useState(false);
  const [showForwardHoSModal, setShowForwardHoSModal] = useState(false);

  // Form states
  const [approvalData, setApprovalData] = useState({
    approved_category: '',
    validity_years: 3,
    conditions: '',
    remarks: '',
  });

  const [rejectionData, setRejectionData] = useState({
    reason: '',
    explanation: '',
  });

  const [queryData, setQueryData] = useState({
    sections: [],
    questions: '',
    response_deadline: '',
  });

  const [remarkData, setRemarkData] = useState({ remarks: '' });
  const [forwardRemarks, setForwardRemarks] = useState('');

  const fetchApplicationDetail = useCallback(async () => {
    try {
      const response = await adminAPI.getApplicationDetail(id);
      setApplication(response.data.application);
      setQueries(response.data.queries || []);
      setAuditLogs(response.data.audit_logs || []);
      setApprovalHistory(response.data.approval_history || []);
    } catch (error) {
      toast.error('Failed to fetch application details');
      navigate('/admin/applications');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchApplicationDetail();
  }, [fetchApplicationDetail]);

  const handleStartReview = async () => {
    setActionLoading(true);
    try {
      await adminAPI.startReview(id);
      toast.success('Review started');
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approvalData.approved_category) {
      toast.error('Please select an approved category');
      return;
    }

    setActionLoading(true);
    try {
      await adminAPI.approveApplication(id, approvalData);
      toast.success('Application approved successfully');
      setShowApproveModal(false);
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionData.reason || !rejectionData.explanation) {
      toast.error('Please provide rejection reason and explanation');
      return;
    }

    setActionLoading(true);
    try {
      await adminAPI.rejectApplication(id, rejectionData);
      toast.success('Application rejected');
      setShowRejectModal(false);
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRaiseQuery = async () => {
    if (!queryData.sections.length || !queryData.questions || !queryData.response_deadline) {
      toast.error('Please fill all required fields');
      return;
    }

    setActionLoading(true);
    try {
      await adminAPI.raiseQuery(id, queryData);
      toast.success('Query raised successfully');
      setShowQueryModal(false);
      setQueryData({ sections: [], questions: '', response_deadline: '' });
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise query');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddRemark = async () => {
    if (!remarkData.remarks) {
      toast.error('Please enter remarks');
      return;
    }

    setActionLoading(true);
    try {
      await adminAPI.addRemark(id, remarkData);
      toast.success('Remark added');
      setShowRemarkModal(false);
      setRemarkData({ remarks: '' });
      fetchApplicationDetail();
    } catch (error) {
      toast.error('Failed to add remark');
    } finally {
      setActionLoading(false);
    }
  };

  // DA → AR → HoS Workflow Handlers
  const handleForwardToAR = async () => {
    setActionLoading(true);
    try {
      await adminAPI.forwardToAR(id, { remarks: forwardRemarks || null });
      toast.success('Application forwarded to Assistant Registrar');
      setShowForwardARModal(false);
      setForwardRemarks('');
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to forward to AR');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForwardToHoS = async () => {
    setActionLoading(true);
    try {
      await adminAPI.forwardToHoS(id, { remarks: forwardRemarks || null });
      toast.success('Application forwarded to Head of Stores');
      setShowForwardHoSModal(false);
      setForwardRemarks('');
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to forward to HoS');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturnToDA = async () => {
    if (!remarkData.remarks) {
      toast.error('Please enter return remarks');
      return;
    }
    setActionLoading(true);
    try {
      await adminAPI.returnToDA(id, remarkData);
      toast.success('Application returned to Dealing Assistant');
      setShowReturnModal(false);
      setRemarkData({ remarks: '' });
      fetchApplicationDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return to DA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await adminAPI.downloadScrutinyReport(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scrutiny_report_${application.application_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSectionToggle = (section, checked) => {
    setQueryData(prev => ({
      ...prev,
      sections: checked
        ? [...prev.sections, section]
        : prev.sections.filter(s => s !== section),
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#003380]" />
      </div>
    );
  }

  const app = application;
  const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.submitted;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-12 w-auto"
              />
              <div className="border-l border-slate-200 pl-4">
                <h1 className="text-base font-bold text-[#003380] font-serif leading-tight">Application Review</h1>
                <p className="text-xs text-slate-500 font-mono">{app.application_number}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-2">
                <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
              </div>
              <Button variant="outline" onClick={handleLogout} size="sm" className="text-slate-600 border-slate-300" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Back button */}
        <Link to="/admin/applications" className="inline-flex items-center text-sm text-slate-600 hover:text-[#003380] mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Applications
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Timeline */}
            <Card className="govt-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusConfig.color} flex items-center gap-1 px-3 py-1`}>
                      <StatusIcon className="h-4 w-4" />
                      {statusConfig.label}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      Submitted: {formatDateTime(app.submitted_at)}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleDownloadReport} data-testid="download-report-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Scrutiny Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Application Details Tabs */}
            <Card className="govt-card overflow-hidden">
              <Tabs defaultValue="basic">
                <div className="border-b border-slate-200 px-4 pt-4">
                  <TabsList className="bg-transparent gap-1 flex-wrap h-auto">
                    <TabsTrigger value="basic" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Basic
                    </TabsTrigger>
                    <TabsTrigger value="address" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Address
                    </TabsTrigger>
                    <TabsTrigger value="signatory" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Signatory
                    </TabsTrigger>
                    <TabsTrigger value="bank" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Bank
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Documents
                    </TabsTrigger>
                    <TabsTrigger value="capability" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Capability
                    </TabsTrigger>
                    <TabsTrigger value="queries" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      Queries
                    </TabsTrigger>
                    <TabsTrigger value="approval-history" className="data-[state=active]:bg-[#003380] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-md">
                      History
                    </TabsTrigger>
                  </TabsList>
                </div>
                <CardContent className="p-6">
                  <TabsContent value="basic" className="mt-0">
                    <div className="grid md:grid-cols-2 gap-4">
                      <InfoRow label="Company Name" value={app.basic_info?.company_name} />
                      <InfoRow label="Business Type" value={app.basic_info?.business_type?.replace('_', ' ')} />
                      <InfoRow label="PAN Number" value={app.basic_info?.pan_number} isSensitive mask={maskPAN} />
                      <InfoRow label="GST Number" value={app.basic_info?.gst_number} isSensitive mask={maskGSTIN} />
                      <InfoRow label="Incorporation Date" value={app.basic_info?.incorporation_date} />
                      <InfoRow label="Annual Turnover" value={app.basic_info?.annual_turnover ? `₹${app.basic_info.annual_turnover} Lakhs` : '-'} />
                      <InfoRow label="Employee Count" value={app.basic_info?.employee_count} />
                    </div>
                    
                    {/* Contact Information - Quick View */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="font-semibold mb-4 text-[#003380]">Contact Information</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <InfoRow label="Contact Person" value={app.authorized_signatory?.name} />
                        <InfoRow label="Designation" value={app.authorized_signatory?.designation} />
                        <InfoRow label="Email Address" value={app.authorized_signatory?.email} highlight />
                        <InfoRow label="Phone Number" value={app.authorized_signatory?.phone} highlight />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="address" className="mt-0">
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold mb-3">Registered Address</h4>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p>{app.registered_address?.address_line1}</p>
                          {app.registered_address?.address_line2 && <p>{app.registered_address.address_line2}</p>}
                          <p>{app.registered_address?.city}, {app.registered_address?.state} - {app.registered_address?.pincode}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Correspondence Address</h4>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p>{app.correspondence_address?.address_line1}</p>
                          {app.correspondence_address?.address_line2 && <p>{app.correspondence_address.address_line2}</p>}
                          <p>{app.correspondence_address?.city}, {app.correspondence_address?.state} - {app.correspondence_address?.pincode}</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="signatory" className="mt-0">
                    <div className="grid md:grid-cols-2 gap-4">
                      <InfoRow label="Name" value={app.authorized_signatory?.name} />
                      <InfoRow label="Designation" value={app.authorized_signatory?.designation} />
                      <InfoRow label="Email" value={app.authorized_signatory?.email} />
                      <InfoRow label="Phone" value={app.authorized_signatory?.phone} />
                      <InfoRow
                        label="Aadhaar"
                        value={app.authorized_signatory?.aadhaar_number}
                        isSensitive
                        mask={maskAadhaar}
                        fallback="Not Provided"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="bank" className="mt-0">
                    <div className="grid md:grid-cols-2 gap-4">
                      <InfoRow label="Bank Name" value={app.bank_details?.bank_name} />
                      <InfoRow label="Branch" value={app.bank_details?.branch_name} />
                      <InfoRow label="Account Number" value={app.bank_details?.account_number} isSensitive mask={maskBankAccount} />
                      <InfoRow label="IFSC Code" value={app.bank_details?.ifsc_code} />
                      <InfoRow label="Account Type" value={app.bank_details?.account_type} />
                    </div>
                  </TabsContent>

                  <TabsContent value="documents" className="mt-0">
                    <div className="space-y-3">
                      {app.compliance_documents?.length > 0 ? (
                        app.compliance_documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-slate-400" />
                              <div>
                                <p className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-slate-500">{doc.file_name || 'Document'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {doc.file_path && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await openUploadedFile(doc.file_path);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || err.message || 'Unable to open document');
                                    }
                                  }}
                                  data-testid={`view-doc-${idx}-btn`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              )}
                              <Badge className={doc.verified ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}>
                                {doc.verified ? 'Verified' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-4">No documents uploaded</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="capability" className="mt-0">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-500">Categories</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {app.capability?.categories?.map(cat => (
                            <Badge key={cat} className="capitalize">{cat}</Badge>
                          )) || <span className="text-slate-400">-</span>}
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-sm text-slate-500">MSME</p>
                          <p className="font-medium">{app.capability?.is_msme ? `Yes (${app.capability.msme_number || 'N/A'})` : 'No'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-sm text-slate-500">Startup</p>
                          <p className="font-medium">{app.capability?.is_startup ? `Yes (${app.capability.startup_dipp_number || 'N/A'})` : 'No'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-sm text-slate-500">OEM</p>
                          <p className="font-medium">{app.capability?.is_oem ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                      {app.capability?.products_services && (
                        <div>
                          <Label className="text-slate-500">Products/Services</Label>
                          <p className="mt-1 text-sm bg-slate-50 rounded-lg p-3">{app.capability.products_services}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="queries" className="mt-0">
                    {queries.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No queries raised</p>
                    ) : (
                      <div className="space-y-4">
                        {queries.map((query) => (
                          <div key={query.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className={query.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                                {query.status === 'pending' ? 'Awaiting Response' : 'Responded'}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                {formatDateTime(query.raised_at)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              <strong>Sections:</strong> {query.sections?.join(', ')}
                            </p>
                            <p className="text-sm text-slate-600 mb-2">
                              <strong>Question:</strong> {query.questions}
                            </p>
                            {query.response_text && (
                              <div className="bg-emerald-50 rounded p-3 mt-2">
                                <p className="text-sm text-emerald-700">
                                  <strong>Response:</strong> {query.response_text}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Approval History Tab - Only for reviewers */}
                  <TabsContent value="approval-history" className="mt-0">
                    {(!approvalHistory || approvalHistory.length === 0) ? (
                      <p className="text-slate-500 text-center py-4">No approval history yet</p>
                    ) : (
                      <div className="space-y-4">
                        {approvalHistory.map((entry, idx) => {
                          const actionColors = {
                            'forwarded_to_ar': 'bg-purple-50 border-purple-200 text-purple-700',
                            'forwarded_to_hos': 'bg-indigo-50 border-indigo-200 text-indigo-700',
                            'returned_to_da': 'bg-amber-50 border-amber-200 text-amber-700',
                            'approved': 'bg-emerald-50 border-emerald-200 text-emerald-700',
                            'rejected': 'bg-red-50 border-red-200 text-red-700'
                          };
                          const colorClass = actionColors[entry.action] || 'bg-slate-50 border-slate-200';
                          
                          return (
                            <div key={idx} className={`border rounded-lg p-4 ${colorClass}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={colorClass}>
                                    {entry.action_label || entry.action?.replace(/_/g, ' ')}
                                  </Badge>
                                  <span className="text-sm font-medium">
                                    by {entry.performed_by_name}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    ({entry.performed_by_role})
                                  </span>
                                </div>
                                <span className="text-xs text-slate-500">
                                  {formatDateTime(entry.timestamp)}
                                </span>
                              </div>
                              {entry.remarks && (
                                <div className="mt-2 bg-white/50 rounded p-3">
                                  <p className="text-sm">
                                    <strong>Remarks:</strong> {entry.remarks}
                                  </p>
                                </div>
                              )}
                              {entry.details && (
                                <div className="mt-2 text-xs text-slate-600">
                                  {entry.details.category && <span>Category: {entry.details.category} | </span>}
                                  {entry.details.validity_years && <span>Validity: {entry.details.validity_years} years | </span>}
                                  {entry.details.reason && <span>Reason: {entry.details.reason}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>

            <AuditTrail logs={auditLogs} />
          </div>

          {/* Action Panel */}
          <div className="space-y-4">
            <Card className="govt-card sticky top-20">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-800">Actions</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {/* Workflow Info */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Approval Workflow</p>
                  <div className="flex items-center gap-1.5 text-xs font-medium">
                    <span className={`px-1.5 py-0.5 rounded ${['submitted','under_review'].includes(app.status) ? 'bg-[#003380] text-white' : 'text-slate-500'}`}>DA</span>
                    <span className="text-slate-300">→</span>
                    <span className={`px-1.5 py-0.5 rounded ${app.status === 'pending_ar' ? 'bg-[#003380] text-white' : 'text-slate-500'}`}>AR</span>
                    <span className="text-slate-300">→</span>
                    <span className={`px-1.5 py-0.5 rounded ${app.status === 'pending_hos' ? 'bg-[#003380] text-white' : app.status === 'approved' ? 'bg-emerald-600 text-white' : 'text-slate-500'}`}>HoS</span>
                  </div>
                </div>

                {/* DA Actions */}
                {user?.role === 'dealing_assistant' && ['submitted', 'under_review'].includes(app.status) && (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                      onClick={() => setShowForwardARModal(true)}
                      disabled={actionLoading}
                      data-testid="forward-to-ar-btn"
                    >
                      Forward to AR
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-600 text-sm h-9" 
                      onClick={() => setShowRejectModal(true)}
                      data-testid="reject-btn"
                    >
                      Reject
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm h-9 hover:text-slate-900" 
                      onClick={() => setShowQueryModal(true)}
                      data-testid="raise-query-btn"
                    >
                      Raise Query
                    </Button>
                  </div>
                )}

                {/* AR Actions */}
                {user?.role === 'assistant_registrar' && app.status === 'pending_ar' && (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                      onClick={() => setShowForwardHoSModal(true)}
                      disabled={actionLoading}
                      data-testid="forward-to-hos-btn"
                    >
                      Forward to HoS
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full border-amber-400 text-amber-600 hover:bg-amber-50 hover:text-amber-600 text-sm h-9" 
                      onClick={() => setShowReturnModal(true)}
                      data-testid="return-to-da-btn"
                    >
                      Return to DA
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-600 text-sm h-9" 
                      onClick={() => setShowRejectModal(true)}
                      data-testid="reject-btn"
                    >
                      Reject
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm h-9 hover:text-slate-900" 
                      onClick={() => setShowQueryModal(true)}
                      data-testid="raise-query-btn"
                    >
                      Raise Query
                    </Button>
                  </div>
                )}

                {/* HoS Actions */}
                {user?.role === 'head_of_stores' && app.status === 'pending_hos' && (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9" 
                      onClick={() => setShowApproveModal(true)}
                      data-testid="approve-btn"
                    >
                      Approve Application
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full border-amber-400 text-amber-600 hover:bg-amber-50 hover:text-amber-600 text-sm h-9" 
                      onClick={() => setShowReturnModal(true)}
                      data-testid="return-to-da-btn"
                    >
                      Return to DA
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-600 text-sm h-9" 
                      onClick={() => setShowRejectModal(true)}
                      data-testid="reject-btn"
                    >
                      Reject
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm h-9 hover:text-slate-900" 
                      onClick={() => setShowQueryModal(true)}
                      data-testid="raise-query-btn"
                    >
                      Raise Query
                    </Button>
                  </div>
                )}

                {/* Super Admin - Full Access */}
                {user?.role === 'super_admin' && ['submitted', 'under_review', 'pending_ar', 'pending_hos'].includes(app.status) && (
                  <div className="space-y-2">
                    {app.status === 'submitted' && (
                      <Button 
                        className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                        onClick={handleStartReview}
                        disabled={actionLoading}
                        data-testid="start-review-btn"
                      >
                        Start Review
                      </Button>
                    )}
                    {['submitted', 'under_review'].includes(app.status) && (
                      <Button 
                        className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                        onClick={() => setShowForwardARModal(true)}
                        disabled={actionLoading}
                        data-testid="forward-to-ar-btn"
                      >
                        Forward to AR
                      </Button>
                    )}
                    {app.status === 'pending_ar' && (
                      <Button 
                        className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                        onClick={() => setShowForwardHoSModal(true)}
                        disabled={actionLoading}
                        data-testid="forward-to-hos-btn"
                      >
                        Forward to HoS
                      </Button>
                    )}
                    {app.status === 'pending_hos' && (
                      <Button 
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9" 
                        onClick={() => setShowApproveModal(true)}
                        data-testid="approve-btn"
                      >
                        Approve
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-600 text-sm h-9" 
                      onClick={() => setShowRejectModal(true)}
                      data-testid="reject-btn"
                    >
                      Reject
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full text-sm h-9 hover:text-slate-900" 
                      onClick={() => setShowQueryModal(true)}
                      data-testid="raise-query-btn"
                    >
                      Raise Query
                    </Button>
                  </div>
                )}

                {/* Legacy roles */}
                {['scrutiny_officer', 'approving_authority'].includes(user?.role) && ['submitted', 'under_review'].includes(app.status) && (
                  <div className="space-y-2">
                    {app.status === 'submitted' && (
                      <Button 
                        className="w-full bg-[#003380] hover:bg-[#002060] text-white text-sm h-9" 
                        onClick={handleStartReview}
                        disabled={actionLoading}
                        data-testid="start-review-btn"
                      >
                        Start Review
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full text-sm h-9 hover:text-slate-900" 
                      onClick={() => setShowQueryModal(true)}
                      data-testid="raise-query-btn"
                    >
                      Raise Query
                    </Button>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full text-sm h-9 mt-1 hover:bg-[#8a0b1e] hover:text-white hover:border-[#8a0b1e]" 
                  onClick={() => setShowRemarkModal(true)}
                  data-testid="add-remark-btn"
                >
                  Add Internal Remark
                </Button>

                {/* Status Info */}
                {app.status === 'approved' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-emerald-700 mb-2">Approval Details</h4>
                    <p className="text-sm text-emerald-600">Category: {app.approved_category}</p>
                    <p className="text-sm text-emerald-600">Validity: {app.validity_years} Year(s)</p>
                    {app.approval_conditions && (
                      <p className="text-sm text-emerald-600">Conditions: {app.approval_conditions}</p>
                    )}
                  </div>
                )}

                {app.status === 'rejected' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-red-700 mb-2">Rejection Details</h4>
                    <p className="text-sm text-red-600">Reason: {app.rejection_reason}</p>
                    {app.rejection_explanation && (
                      <p className="text-sm text-red-600 mt-1">{app.rejection_explanation}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Vendor Application</DialogTitle>
            <DialogDescription>
              This will approve the vendor for government procurement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Approved Category *</Label>
              <Select
                value={approvalData.approved_category}
                onValueChange={(value) => setApprovalData(prev => ({ ...prev, approved_category: value }))}
              >
                <SelectTrigger data-testid="approval-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods">Goods</SelectItem>
                  <SelectItem value="services">Services</SelectItem>
                  <SelectItem value="works">Works</SelectItem>
                  <SelectItem value="consultancy">Consultancy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Validity Period *</Label>
              <Select
                value={approvalData.validity_years.toString()}
                onValueChange={(value) => setApprovalData(prev => ({ ...prev, validity_years: parseInt(value) }))}
              >
                <SelectTrigger data-testid="validity-years-select">
                  <SelectValue placeholder="Select validity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Year</SelectItem>
                  <SelectItem value="3">3 Years</SelectItem>
                  <SelectItem value="5">5 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Conditions (Optional)</Label>
              <Textarea
                value={approvalData.conditions}
                onChange={(e) => setApprovalData(prev => ({ ...prev, conditions: e.target.value }))}
                placeholder="Any conditions for approval..."
                data-testid="approval-conditions-textarea"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={approvalData.remarks}
                onChange={(e) => setApprovalData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Internal remarks..."
                data-testid="approval-remarks-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleApprove}
              disabled={actionLoading}
              data-testid="confirm-approve-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">Reject Vendor Application</DialogTitle>
            <DialogDescription>
              Please provide a mandatory reason for rejection
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Select
                value={rejectionData.reason}
                onValueChange={(value) => setRejectionData(prev => ({ ...prev, reason: value }))}
              >
                <SelectTrigger data-testid="rejection-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REJECTION_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Detailed Explanation *</Label>
              <Textarea
                value={rejectionData.explanation}
                onChange={(e) => setRejectionData(prev => ({ ...prev, explanation: e.target.value }))}
                placeholder="Provide detailed explanation for rejection..."
                rows={4}
                data-testid="rejection-explanation-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button 
              className="govt-btn-danger" 
              onClick={handleReject}
              disabled={actionLoading}
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Query Modal */}
      <Dialog open={showQueryModal} onOpenChange={setShowQueryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Raise Clarification Query</DialogTitle>
            <DialogDescription>
              Select sections and add your questions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sections Requiring Clarification *</Label>
              <div className="grid grid-cols-2 gap-2">
                {SECTIONS_FOR_QUERY.map(section => (
                  <div key={section.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`section-${section.value}`}
                      checked={queryData.sections.includes(section.value)}
                      onCheckedChange={(checked) => handleSectionToggle(section.value, checked)}
                    />
                    <Label htmlFor={`section-${section.value}`} className="font-normal text-sm">
                      {section.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Questions / Remarks *</Label>
              <Textarea
                value={queryData.questions}
                onChange={(e) => setQueryData(prev => ({ ...prev, questions: e.target.value }))}
                placeholder="Enter your questions or remarks..."
                rows={4}
                data-testid="query-questions-textarea"
              />
            </div>
            <div className="space-y-2">
              <Label>Response Deadline *</Label>
              <Input
                type="date"
                value={queryData.response_deadline}
                onChange={(e) => setQueryData(prev => ({ ...prev, response_deadline: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                data-testid="query-deadline-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQueryModal(false)}>Cancel</Button>
            <Button 
              className="govt-btn-primary" 
              onClick={handleRaiseQuery}
              disabled={actionLoading}
              data-testid="confirm-query-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remark Modal */}
      <Dialog open={showRemarkModal} onOpenChange={setShowRemarkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Internal Remark</DialogTitle>
            <DialogDescription>
              This remark will be visible in the audit log
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Remarks *</Label>
              <Textarea
                value={remarkData.remarks}
                onChange={(e) => setRemarkData({ remarks: e.target.value })}
                placeholder="Enter your remarks..."
                rows={4}
                data-testid="remark-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemarkModal(false)}>Cancel</Button>
            <Button 
              className="govt-btn-primary" 
              onClick={handleAddRemark}
              disabled={actionLoading}
              data-testid="confirm-remark-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Remark
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return to DA Modal */}
      <Dialog open={showReturnModal} onOpenChange={setShowReturnModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Dealing Assistant</DialogTitle>
            <DialogDescription>
              The application will be returned to DA for additional review or corrections
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Return *</Label>
              <Textarea
                value={remarkData.remarks}
                onChange={(e) => setRemarkData({ remarks: e.target.value })}
                placeholder="Enter reason for returning to DA..."
                rows={4}
                data-testid="return-remarks-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnModal(false)}>Cancel</Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white" 
              onClick={handleReturnToDA}
              disabled={actionLoading}
              data-testid="confirm-return-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Return to DA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward to AR Modal */}
      <Dialog open={showForwardARModal} onOpenChange={setShowForwardARModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forward to Assistant Registrar</DialogTitle>
            <DialogDescription>
              Add remarks for the AR (optional)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={forwardRemarks}
                onChange={(e) => setForwardRemarks(e.target.value)}
                placeholder="Enter any remarks for the AR..."
                rows={3}
                data-testid="forward-ar-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForwardARModal(false); setForwardRemarks(''); }}>Cancel</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white" 
              onClick={handleForwardToAR}
              disabled={actionLoading}
              data-testid="confirm-forward-ar-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Forward to AR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward to HoS Modal */}
      <Dialog open={showForwardHoSModal} onOpenChange={setShowForwardHoSModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forward to Head of Stores</DialogTitle>
            <DialogDescription>
              Add remarks for the HoS (optional)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={forwardRemarks}
                onChange={(e) => setForwardRemarks(e.target.value)}
                placeholder="Enter any remarks for the HoS..."
                rows={3}
                data-testid="forward-hos-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForwardHoSModal(false); setForwardRemarks(''); }}>Cancel</Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700 text-white" 
              onClick={handleForwardToHoS}
              disabled={actionLoading}
              data-testid="confirm-forward-hos-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Forward to HoS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component
function InfoRow({ label, value, highlight, isSensitive, mask, fallback = '-' }) {
  const [revealed, setRevealed] = useState(false);
  const canToggle = isSensitive && canToggleSensitiveValue(value);
  const shown = canToggle ? displaySensitiveValue(value, mask, revealed) : value;

  useEffect(() => {
    setRevealed(false);
  }, [value]);

  return (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
      <p className="text-sm text-slate-500 flex items-center gap-1">
        {label}
        {canToggle && (
          <button
            type="button"
            onClick={() => setRevealed((current) => !current)}
            className="inline-flex items-center text-slate-400 hover:text-slate-600"
            aria-pressed={revealed}
            aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
            title={revealed ? 'Hide' : 'Show'}
            data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {revealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </button>
        )}
      </p>
      <p className={`font-medium ${highlight ? 'text-[#003380]' : ''} ${isSensitive ? 'font-mono text-sm' : ''}`}>
        {shown || fallback}
      </p>
    </div>
  );
}
